import { useEffect, useMemo, useRef, useState } from 'react';
import { useAtom } from 'jotai';
import { useUpdateAtom, useAtomValue } from 'jotai/utils';
import { useDebounce } from 'use-debounce';
import { useHistory, useLocation } from 'react-router-dom';
import { useQueryClient } from 'react-query';

import { useAccessToken } from 'src/hooks/useAccessToken';
import useModal from 'src/hooks/useModal';
import useRoleManager from 'src/hooks/useRoleManager';
import { useAlerts } from 'src/common/AlertManager';
import useErrorMessages from 'src/hooks/useErrorsMessage';
import { useCollectionQuery } from 'src/api/queries/collectionsQueries';
import { isRefetchingAtom, refreshAtom } from 'src/shared/refresh';
import { selectedAssetsAtom } from 'src/shared/selectedAssets';
import { deletedAssetsAtom } from 'src/shared/deletedAssets';
import { TSorting } from 'src/types/TSorting';
import { getThumbnailUrl, thumbnailVersion } from 'src/utils/previews';
import {
  dashboardFiltersAtom,
  setFilterAtom,
  serverFiltersAtom,
  DEFAULT_LIMIT_PER_PAGE,
  type FilterPatch,
} from 'src/shared/dashboardFilters';

import { ITEMS_PER_PAGE_LOCAL_KEY } from './constants';

const checkImage = (url: string) =>
  new Promise<string>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(url);
    img.onerror = () => reject();
    img.src = url;
  });

export const useCachePreviewImages = (data: any) => {
  const accessToken = useAccessToken();

  const [firstTime, setFirstTime] = useState(true);

  if (data && data.result && firstTime) {
    for (const assetId of data.result) {
      const url = getThumbnailUrl({
        width: 800,
        height: 600,
        id: assetId,
        jwt: accessToken,
        version: thumbnailVersion(data.extended_result?.[assetId]),
      });

      checkImage(url);
    }

    setFirstTime(false);
  }
};

/**
 * Translate the legacy `<input type="datetime-local">` string ↔ ISO 8601
 * stored on the canonical atom. Replaces the per-field handlers that lived
 * directly on the dashboard.
 */
function useDateRangeInputs(
  range: { start?: string; end?: string },
  apply: (patch: FilterPatch) => void,
) {
  const [startInput, setStartInput] = useState('');
  const [endInput, setEndInput] = useState('');

  // Keep visible inputs in sync if the canonical state is reset elsewhere
  // (e.g. AI intent, Clear all, pill X).
  useEffect(() => {
    if (!range.start) setStartInput('');
    if (!range.end) setEndInput('');
  }, [range.start, range.end]);

  const handleStartDateChange = (value: string) => {
    const newStart = value ? new Date(value).toISOString() : undefined;
    setStartInput(value || '');
    apply({
      uploadTimeRange: { start: newStart, end: range.end },
      page: 1,
      hash: '',
    });
  };

  const handleEndDateChange = (value: string) => {
    let newEnd: string | undefined;
    if (value) {
      const date = new Date(value);
      date.setMilliseconds(999);
      newEnd = date.toISOString();
    }
    setEndInput(value || '');
    apply({
      uploadTimeRange: { start: range.start, end: newEnd },
      page: 1,
      hash: '',
    });
  };

  const clearStartDate = () => {
    setStartInput('');
    apply({
      uploadTimeRange: { start: undefined, end: range.end },
      page: 1,
      hash: '',
    });
  };

  const clearEndDate = () => {
    setEndInput('');
    apply({
      uploadTimeRange: { start: range.start, end: undefined },
      page: 1,
      hash: '',
    });
  };

  const clearRangeDate = () => {
    setStartInput('');
    setEndInput('');
    apply({ uploadTimeRange: {}, page: 1, hash: '' });
  };

  return {
    startDateInputValue: startInput,
    endDateInputValue: endInput,
    handleStartDateChange,
    handleEndDateChange,
    clearStartDate,
    clearEndDate,
    clearRangeDate,
  };
}

/**
 * Local debounced text-input buffer that flushes into the canonical atom
 * after the debounce window. Reads `committedValue` once on mount so AI
 * intents / Clear all / pill removals can drive the input value too.
 */
function useDebouncedFilterField(
  committedValue: string,
  applyDebounced: (next: string) => void,
  delayMs = 800,
): [string, (next: string) => void] {
  const [draft, setDraft] = useState(committedValue);
  const [debounced] = useDebounce(draft, delayMs);
  const lastCommitted = useRef(committedValue);

  // External writes (AI, Clear all, pill X) push the draft back in sync.
  useEffect(() => {
    if (committedValue !== lastCommitted.current) {
      setDraft(committedValue);
      lastCommitted.current = committedValue;
    }
  }, [committedValue]);

  // Local edits flush into the atom after debouncing.
  useEffect(() => {
    if (debounced !== lastCommitted.current) {
      lastCommitted.current = debounced;
      applyDebounced(debounced);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);

  return [draft, setDraft];
}

const useComponentHooks = () => {
  const [filterState, setFilterState] = useAtom(dashboardFiltersAtom);
  const apply = useUpdateAtom(setFilterAtom);
  const serverPayload = useAtomValue(serverFiltersAtom);

  // Initial limit_per_page hydration from localStorage. We do this once so
  // returning users keep their preferred page size.
  useEffect(() => {
    const stored = localStorage.getItem(ITEMS_PER_PAGE_LOCAL_KEY);
    if (stored) {
      const limit = Number(stored);
      if (Number.isFinite(limit) && limit !== filterState.limitPerPage) {
        apply({ limitPerPage: limit });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // `/assets?awaiting_release=1` — the link on the "assets awaiting release"
  // notice and daily email — opens the list with the Awaiting release filter
  // on. Applied once, then dropped from the URL so it does not stick.
  const location = useLocation();
  const history = useHistory();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('awaiting_release') !== '1') return;
    apply({ pendingReleaseOnly: true, page: 1, hash: '' });
    params.delete('awaiting_release');
    const rest = params.toString();
    history.replace({ pathname: location.pathname, search: rest ? `?${rest}` : '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // ----- UI-only state (does not affect the search payload) -----------------

  const [isDeletingAssets, setIsDeletingAssets] = useState(false);
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [totalPages, setTotalPages] = useState(1);

  // Sorting maps to `sortBy` in the atom; surface the existing tuple shape
  // so the LayoutViewSwitcher and SearchBar handlers don't need to change.
  const sorting: TSorting = useMemo(() => {
    const [, ...rest] = filterState.sortBy[0]?.split('_') ?? [];
    const last = rest.pop() ?? 'DESC';
    const field = filterState.sortBy[0]?.replace(`_${last}`, '') ?? 'upload_time_range';
    return [field as TSorting[0], last as TSorting[1]];
  }, [filterState.sortBy]);

  const setSorting = (next: TSorting) => {
    apply({ sortBy: [next.join('_')] });
  };

  // ----- Debounced text inputs ---------------------------------------------

  const [search, setSearch] = useDebouncedFilterField(
    filterState.keyword,
    (next) => apply({ keyword: next, page: 1, hash: '' }),
  );

  const [fileName, setFileName] = useDebouncedFilterField(
    filterState.fileName,
    (next) => apply({ fileName: next, page: 1, hash: '' }),
  );

  const [customContentInput, setCustomContentInput] = useDebouncedFilterField(
    filterState.customContentInput,
    (next) => apply({ customContentInput: next, page: 1, hash: '' }),
  );

  // ----- Date inputs --------------------------------------------------------

  const dateInputs = useDateRangeInputs(filterState.uploadTimeRange, apply);

  // ----- Face search --------------------------------------------------------

  const setFace = (face: string | null) => {
    apply({ face, page: 1, hash: '' });
  };

  const enhancedFaceSearch = (img: any) => {
    apply({ face: img, page: 1, hash: '' });
  };

  const enhancedFaceRemoval = () => {
    apply({ face: null, freeFormFilters: null, page: 1, hash: '' });
  };

  // Clears just the schema selection + free-form filters, preserving everything
  // else. Used by Cancel buttons inside the schema/face search forms.
  const clearSchemaAndFreeForm = () => {
    apply({
      schema: null,
      freeFormFilters: null,
      derivedOnly: false,
      pendingReleaseOnly: false,
      limitPerPage: DEFAULT_LIMIT_PER_PAGE,
      page: 1,
      hash: '',
    });
  };

  // ----- Selected / deleted assets atoms ------------------------------------

  const [selectedAssets, updateSelectedAssets] = useAtom(selectedAssetsAtom);
  const [deletedAssets, updateDeletedAssets] = useAtom(deletedAssetsAtom);

  return {
    accessToken: useAccessToken(),
    roleManager: useRoleManager(),
    history: useHistory(),
    modal: useModal(),
    queryClient: useQueryClient(),
    alerts: useAlerts(),
    errorMessages: useErrorMessages(),

    // Canonical filter state and payload for `useSearchQuery`.
    filterState,
    setFilterState,
    apply,
    serverPayload,

    // Date input glue.
    ...dateInputs,

    // Face helpers.
    enhancedFaceSearch,
    enhancedFaceRemoval,
    clearSchemaAndFreeForm,

    queries: {
      collections: useCollectionQuery(),
    },

    refs: {
      searchHash: useRef(''),
    },

    state: {
      // Debounced text inputs (drafts).
      search,
      setSearch,
      fileName,
      setFileName,
      customContentInput,
      setCustomContentInput,

      // Pure UI state.
      isDeletingAssets,
      setIsDeletingAssets,
      filtersVisible,
      setFiltersVisible,
      totalPages,
      setTotalPages,

      // Sorting (mapped to `sortBy`).
      sorting,
      setSorting,

      // Face image (committed).
      face: filterState.face,
      setFace,

      // Refresh / refetch atoms.
      refreshValue: useAtomValue(refreshAtom),
      setIsRefetching: useUpdateAtom(isRefetchingAtom),

      // Asset-selection atoms.
      selectedAssets,
      updateSelectedAssets,
      deletedAssets,
      updateDeletedAssets,
    },
  };
};

export default useComponentHooks;
